/**
 * SparkleBurst — theme-colored twinkle particles that fire once on
 * mouse-over (desktop) or press (touch). Meant to sit as an absolute
 * layer inside any button that wants a bit of glint.
 *
 * Usage:
 *   const sparkle = useSparkleTrigger();
 *   <button
 *     onPointerEnter={sparkle.onEnter}
 *     onPointerDown={sparkle.onPress}
 *     className="group relative ..."
 *   >
 *     <SparkleBurst trigger={sparkle.count} />
 *     ...
 *   </button>
 *
 * Design constraints (matches user's polish spec):
 *   * Transform / opacity only (60 fps).
 *   * ``prefers-reduced-motion`` → nothing renders, hook becomes a no-op.
 *   * On desktop: fires on ``pointerenter``. Throttled — a fresh burst
 *     only fires once per 900 ms even if the mouse hovers back and forth.
 *   * On touch: pointerdown fires it (bypasses the throttle so every tap
 *     gets its own sparkle).
 *   * Theme-agnostic via the CSS var ``--accent``.
 */
import { useCallback, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

function usePrefersReducedMotion() {
  const [reduced] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    try {
      return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    } catch {
      return false;
    }
  });
  return reduced;
}

// Eight fixed points around the FRAME edges (not the middle) — the
// wrapper sits at -inset-4 so these coords intentionally cluster near
// the corners / outside edges where the button's own overflow-hidden
// interior can't clip them. Bumped sizes 4-6 → 6-9 so they read
// against a bright hover glow (e.g. the −1 button's rose ring).
// A single set of coords used across ALL bursts keeps the visual
// language consistent without introducing runtime randomness on
// every render (which would cause layout thrash).
const POINTS = [
  { x: 92, y: 10, delay: 0.00, size: 8 },
  { x: 8,  y: 15, delay: 0.06, size: 7 },
  { x: 4,  y: 82, delay: 0.14, size: 8 },
  { x: 88, y: 88, delay: 0.10, size: 7 },
  { x: 50, y: -4, delay: 0.18, size: 6 },
  { x: 50, y: 102, delay: 0.22, size: 6 },
  { x: 96, y: 50, delay: 0.04, size: 6 },
  { x: 2,  y: 50, delay: 0.16, size: 6 },
];

export function useSparkleTrigger() {
  const reduced = usePrefersReducedMotion();
  const [count, setCount] = useState(0);
  const lastAt = useRef(0);

  const fire = useCallback(
    (force = false) => {
      if (reduced) return;
      const now =
        typeof performance !== "undefined" ? performance.now() : Date.now();
      // Throttle to one burst / 900 ms on hover-repeats. `force=true`
      // (used from pointerdown / press) bypasses this so every tap
      // guarantees a fresh burst.
      if (!force && now - lastAt.current < 900) return;
      lastAt.current = now;
      setCount((c) => c + 1);
    },
    [reduced],
  );

  return {
    count,
    onEnter: () => fire(false),
    onPress: () => fire(true),
    reduced,
  };
}

/**
 * The visual layer. `trigger` should be a monotonically increasing
 * number (typically produced by useSparkleTrigger.count) — every
 * distinct value spawns a fresh keyed burst that animates once and
 * self-removes.
 *
 * `variant`:
 *   * ``dot``  (default) — small square glints tinted with the accent
 *     color. Works everywhere.
 *   * ``star`` — four-point CSS stars, slightly larger. Reads better
 *     inside big keycaps.
 */
export default function SparkleBurst({ trigger = 0, variant = "dot" }) {
  if (!trigger) return null;
  return (
    <span
      aria-hidden
      className="pointer-events-none absolute inset-0 overflow-visible"
    >
      <AnimatePresence>
        <span key={trigger} className="absolute inset-0">
          {POINTS.map((p, i) => (
            <motion.span
              key={`${trigger}-${i}`}
              initial={{ opacity: 0, scale: 0, rotate: 0 }}
              animate={{
                opacity: [0, 1, 0],
                scale: [0, 1.35, 0.4],
                rotate: [0, 180, 320],
                y: [0, -8, -18],
              }}
              exit={{ opacity: 0 }}
              transition={{
                duration: 1.05,
                ease: [0.16, 1, 0.3, 1],
                delay: p.delay,
              }}
              className="absolute"
              style={{
                left: `${p.x}%`,
                top: `${p.y}%`,
                width: `${p.size}px`,
                height: `${p.size}px`,
                marginLeft: `-${p.size / 2}px`,
                marginTop: `-${p.size / 2}px`,
                // Bright WHITE core with a fat accent halo — reads
                // clearly against BOTH a dark background AND a bright
                // hover-glow ring (e.g. −1's rose boxShadow). Without
                // this contrast the sparkles washed into the ring and
                // looked invisible.
                background: variant === "star" ? "transparent" : "#ffffff",
                borderRadius: variant === "star" ? undefined : "50%",
                boxShadow:
                  "0 0 4px #ffffff, 0 0 10px rgb(var(--accent) / 1), 0 0 18px rgb(var(--accent) / 0.65), 0 0 28px rgb(var(--accent) / 0.35)",
                // Hardware-accelerated compositor layer — sparkles
                // fire in bursts, so keeping them promoted avoids
                // re-rasterizing at every keyframe on 120/144Hz.
                willChange: "transform, opacity",
                clipPath:
                  variant === "star"
                    ? "polygon(50% 0%, 60% 40%, 100% 50%, 60% 60%, 50% 100%, 40% 60%, 0% 50%, 40% 40%)"
                    : undefined,
              }}
            />
          ))}
        </span>
      </AnimatePresence>
    </span>
  );
}
